import React, { useEffect, useState, useContext } from 'react';
import { collection, query, where, getDocs, doc, deleteDoc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../Firebase Functions/firebase';
import { AuthContext } from "../hooks/AuthProvider";
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { isIncidentReportCompleted } from '../Quickbase Functions/IncidentReportQBUpdate';

// Styled Components
const Container = styled.div`
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 600px) {
    padding: 12px;
  }
`;



const Title = styled.h2`
  text-align: center;
  color: #296b1a;
`;

const DraftList = styled.div`
  margin-top: 20px;
`;

const DraftItem = styled.div`
  background: white;
  padding: 15px;
  margin-bottom: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 600px) {
    padding: 12px;
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 8px;
  }
`;


const NewReportButton = styled.button`
  display: block;
  margin: 20px auto;
  background-color: #007820;
  color: white;
  border: none;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    background-color: #16324f;
  }

  @media (max-width: 600px) {
    width: 100%;
    text-align: center;
  }
`;

const BaseButton = styled.button`
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  white-space: nowrap;

  @media (max-width: 600px) {
    width: 100%;
    font-size: 15px;
  }
`;



const ResumeButton = styled(BaseButton)`
  background-color: #296b1a;

  &:hover {
    background-color: #1f4e13;
  }
`;

const DeleteButton = styled(BaseButton)`
  background-color: #c0392b;

  &:hover {
    background-color: #a93226;
  }
`;




const Message = styled.p`
  text-align: center;
  color: #555;
`;

const StatusText = styled.span`
  font-weight: bold;
  color: ${props => (props.submitted ? 'green' : 'red')};
  display: inline-block;
  margin-top: 8px;
`;


function ReportDrafts() {
  const { currentUser } = useContext(AuthContext);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;

    const fetchDrafts = async () => {
  try {
    const q = query(
      collection(db, "incidentReports"),
      where("createdBy", "==", currentUser.email),
      where("status", "in", ["draft", "submitted"])
    );

    const querySnapshot = await getDocs(q);

    const draftDocs = querySnapshot.docs;

    const draftsWithQuickbaseStatus = await Promise.all(
      draftDocs.map(async (docSnap) => {
        const data = docSnap.data();
        const id = docSnap.id;

        let isCompletedInQuickbase = false;
        try {
          isCompletedInQuickbase = await isIncidentReportCompleted(id);
        } catch (err) {
          console.warn(`Quickbase status check failed for ${id}`, err);
        }

        return {
          id,
          ...data,
          isCompletedInQuickbase,
          hasCheckRequestDraft: data.attachments?.checkRequestAttached === true,
          checkRequestCompleted: data.checkRequestCompleted === true,
          hasPayoutRequestDraft: data.attachments?.payoutRequestAttached === true,
          payoutRequestCompleted: data.payoutRequestCompleted === true,
          hasVendorRequestDraft: data.attachments?.vendorRequestAttached === true,
          vendorRequestCompleted: data.vendorRequestCompleted === true,
          payrollDeductionCompleted: data.payrollDeductionCompleted === true,
          hasPayrollDeductionDraft: data.attachments?.payrollDeductionAttached === true,
        };
      })
    );

    setDrafts(draftsWithQuickbaseStatus);
  } catch (error) {
    console.error('Error fetching drafts:', error);
  } finally {
    setLoading(false);
  }
};

    
    
    

    fetchDrafts();
  }, [currentUser]);

  const handleDisregardCheckRequest = async (incidentId) => {
    const confirm = window.confirm('Are you sure you want to disregard this check request?');
    if (!confirm) return;
  
    try {
      await updateDoc(doc(db, "checkRequestForms", incidentId), {
        status: "disregarded",
      });
      setDrafts(prevDrafts =>
        prevDrafts.map(d => d.id === incidentId ? { ...d, hasCheckRequestDraft: false } : d)
      );
    } catch (error) {
      console.error('Error disregarding check request:', error);
      alert('Failed to disregard the check request. Please try again.');
    }
  };

  const handleDisregardPayoutRequest = async (incidentId) => {
    const confirm = window.confirm('Are you sure you want to disregard this P.O. request?');
    if (!confirm) return;
  
    try {
      await updateDoc(doc(db, "payoutRequests", incidentId), {
        status: "disregarded",
      });
  
      setDrafts(prevDrafts =>
        prevDrafts.map(d =>
          d.id === incidentId ? { ...d, hasPayoutRequestDraft: false } : d
        )
      );
    } catch (error) {
      console.error('Error disregarding P.O. request:', error);
      alert('Failed to disregard the P.O. request. Please try again.');
    }
  };

  const handleDisregardPayrollDeduction = async (incidentId) => {
    const confirm = window.confirm('Are you sure you want to disregard this Payroll Deduction');
    if (!confirm) return;
  
    try {
      await updateDoc(doc(db, "payrollDeductions", incidentId), {
        status: "disregarded",
      });
  
      setDrafts(prevDrafts =>
        prevDrafts.map(d =>
          d.id === incidentId ? { ...d, hasPayrollDeductionDraft: false } : d
        )
      );
    } catch (error) {
      console.error('Error disregarding Payroll Deduction:', error);
      alert('Failed to disregard the Payroll Deduction. Please try again.');
    }
  };
  

  const handleDisregardVendorRequest = async (incidentId) => {
    const confirm = window.confirm('Are you sure you want to disregard this vendor request?');
    if (!confirm) return;
  
    try {
      await updateDoc(doc(db, "vendorPaymentRequests", incidentId), {
        status: "disregarded",
      });
  
      setDrafts(prevDrafts =>
        prevDrafts.map(d =>
          d.id === incidentId ? { ...d, hasVendorRequestDraft: false } : d
        )
      );
    } catch (error) {
      console.error('Error disregarding vendor request:', error);
      alert('Failed to disregard the vendor request. Please try again.');
    }
  };
  
  

  const handleDelete = async (draftId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this draft?');
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "incidentReports", draftId));
      setDrafts(prevDrafts => prevDrafts.filter(draft => draft.id !== draftId));
    } catch (error) {
      console.error('Error deleting draft:', error);
      alert('Failed to delete the draft. Please try again.');
    }
  };

  

  return (
    <Container>
      <Title>Incident Report Hub</Title>
      <NewReportButton onClick={() => navigate('/incident-report')}>
        + Create New Incident Report
      </NewReportButton>
      {loading ? (
        <Message>Loading drafts...</Message>
      ) : drafts.length === 0 ? (
        <Message>No drafts found. Start a new incident report!</Message>
      ) : (
        <DraftList>
          {drafts.map(draft => (
            <DraftItem key={draft.id}>
              <strong>{draft.employee?.name || 'Unnamed Report'}</strong> <br />
              Saved on: {new Date(draft.timestamp).toLocaleString()} <br />
              <StatusText submitted={draft.status === "submitted"}>
                {draft.status === "submitted" ? "Submitted" : "Not Submitted"}
              </StatusText>

              <ButtonGroup>
              {!draft.isCompletedInQuickbase && (
              <ResumeButton onClick={() => navigate(`/incident-report/resume/${draft.id}`)}>
                Edit Report
              </ResumeButton>
              )}



<ResumeButton
  onClick={() => navigate(`/incident-report/view/${draft.id}`)}
  style={{ backgroundColor: '#3b7bbf' }}
>
  View Report
</ResumeButton>

{/*
<DeleteButton onClick={() => handleDelete(draft.id)}>
    Delete Report
  </DeleteButton>
  */}

  {/* Check Request */}
  {draft.hasCheckRequestDraft && !draft.checkRequestCompleted && (
    <>
      <ResumeButton
        onClick={() => navigate("/check-request-form", { state: { incidentId: draft.id } })}
        style={{ backgroundColor: "#2c3e50" }}
      >
        Complete Check Request
      </ResumeButton>
      <DeleteButton onClick={() => handleDisregardCheckRequest(draft.id)}>
        Disregard Check Request
      </DeleteButton>
    </>
  )}
  {/* Payroll Deduction */}
  {draft.hasPayrollDeductionDraft && !draft.payrollDeductionCompleted && (
    <>
      <ResumeButton
        onClick={() => navigate("/payroll-deduction", { state: { incidentId: draft.id } })}
        style={{ backgroundColor: "#2c3e50" }}
      >
        Complete Payroll Deduction
      </ResumeButton>
      <DeleteButton onClick={() => handleDisregardPayrollDeduction(draft.id)}>
        Disregard Payroll Deduction
      </DeleteButton>
    </>
  )}

  {/* Payout Request */}
  {draft.hasPayoutRequestDraft && !draft.payoutRequestCompleted && (
    <>
      <ResumeButton
        onClick={() => navigate("/payout-request", { state: { incidentId: draft.id } })}
        style={{ backgroundColor: "#6d4c41" }}
      >
        Complete P.O. Request
      </ResumeButton>
      <DeleteButton onClick={() => handleDisregardPayoutRequest(draft.id)}>
        Disregard P.O. Request
      </DeleteButton>
    </>
  )}

  {/* Vendor Request */}
  {draft.hasVendorRequestDraft && !draft.vendorRequestCompleted && (
    <>
      <ResumeButton
        onClick={() => navigate("/vendor-request", { state: { incidentId: draft.id } })}
        style={{ backgroundColor: "#8e44ad" }}
      >
        Complete Vendor Request
      </ResumeButton>
      <DeleteButton onClick={() => handleDisregardVendorRequest(draft.id)}>
        Disregard Vendor Request
      </DeleteButton>
    </>
    
  )}
</ButtonGroup>

            </DraftItem>
          ))}
        </DraftList>
      )}
    </Container>
  );
  
  
  
}

export default ReportDrafts;
